import { useEffect, useMemo, useState } from 'react';

import { OcChatConfig } from '../../../types/oc';
import { getPrivacySettings } from '../services/ocEndpoints';
import { OcPrivacySettings } from '../types/apiTypes';

// TODO: Consider extracting 'loading' | 'success' | 'error' into a shared enum used across the codebase.
/** Result of fetching privacy from Crest; consumers branch on status instead of a separate "loaded" boolean. */
export type PrivacyResult =
  | { status: 'loading'; config: OcChatConfig }
  | { status: 'success'; config: OcChatConfig }
  | { status: 'error'; error: Error; config: OcChatConfig };

/**
 * The OC widget always receives privacy from Crest (GetPrivacySettings); it cannot get or set
 * privacy any other way. This hook fetches from Crest and merges isPrivate/isAnonymous into config.
 *
 * - Production: fetches GetPrivacySettings, merges response into config. While loading, treats as
 *   restrictive (true) so history is not shown until Crest responds. If the fetch fails, privacyError
 *   is set and the widget should treat it as invalid (like authentication failure).
 * - Preview: no Crest call; returns config as-is (preview may pass isPrivate/isAnonymous for testing).
 *
 * @param isAuthAvailable - When false, skips the API call entirely (unauthenticated users
 *   will see the login screen, so privacy settings are irrelevant and the call would 401).
 */
export function useEffectivePrivacyConfig(config: OcChatConfig, isAuthAvailable: boolean): PrivacyResult {
  const isPreview = config.mode === 'preview';

  const [backendPrivacy, setBackendPrivacy] = useState<OcPrivacySettings | null>(
    () => (isPreview ? {} : null),
  );
  const [privacyError, setPrivacyError] = useState<Error | null>(null);

  useEffect(() => {
    if (isPreview) {
      setBackendPrivacy({});
      setPrivacyError(null);
      return;
    }

    // TODO: Consider adding a 'skipped' status to PrivacyResult so this state is
    // distinguishable from a genuine in-flight fetch. Currently falls through to
    // status: 'loading', but OcFabWidget bypasses the privacy gate for unauthenticated
    // users so the restrictive defaults don't leak.
    if (!isAuthAvailable) {
      setBackendPrivacy(null);
      setPrivacyError(null);
      return;
    }

    let cancelled = false;
    setPrivacyError(null);
    getPrivacySettings(config)
      .then((data) => {
        if (!cancelled) {
          setBackendPrivacy(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPrivacyError(err instanceof Error ? err : new Error(String(err)));
          setBackendPrivacy(null);
        }
      });

    // Ignore stale responses if unmounted or deps changed
    return () => {
      cancelled = true;
    };
  }, [config, isPreview, isAuthAvailable]);

  return useMemo((): PrivacyResult => {
    if (isPreview) {
      return { status: 'success', config };
    }
    if (privacyError) {
      return { status: 'error', error: privacyError, config: { ...config, isPrivate: true, isAnonymous: true } };
    }
    if (backendPrivacy === null) {
      return { status: 'loading', config: { ...config, isPrivate: true, isAnonymous: true } };
    }
    return {
      status: 'success',
      config: {
        ...config,
        isPrivate: backendPrivacy.isPrivate,
        isAnonymous: backendPrivacy.isAnonymous,
      },
    };
  }, [config, isPreview, backendPrivacy, privacyError]);
}
